import type { Product, ProductCategory } from "~/config/products";

export type SeoInput = {
  title: string;
  description: string;
  path?: string;
  type?: "website" | "article" | "product";
  image?: string;
  imageAlt?: string;
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  author?: string;
};

export type Breadcrumb = { name: string; path: string };

const configuredOrigin =
  import.meta.env.VITE_SITE_URL ?? "https://hostright.de";
export const siteOrigin = configuredOrigin.replace(/\/$/, "");

export const seoConfig = {
  name: import.meta.env.VITE_SITE_NAME ?? "HostRight",
  defaultTitle: "HostRight | Web hosting with room to grow",
  defaultDescription:
    "Simple, reliable web hosting for websites, stores, and small businesses.",
  defaultImage:
    import.meta.env.VITE_DEFAULT_OG_IMAGE ?? "/images/og-default.svg",
  locale: import.meta.env.VITE_SITE_LOCALE ?? "en_GB",
  googleVerification: import.meta.env.VITE_GOOGLE_SITE_VERIFICATION,
  bingVerification: import.meta.env.VITE_BING_SITE_VERIFICATION,
} as const;

export function absoluteUrl(path = "/") {
  return new URL(path, siteOrigin).toString();
}

/**
 * The one URL policy used by page metadata and the sitemap.
 *
 * Content pages are canonicalized with a trailing slash. File endpoints such
 * as sitemap.xml and robots.txt retain their extension and do not get one.
 */
export function canonicalPath(path = "/") {
  const pathname = path.split(/[?#]/, 1)[0] || "/";
  if (pathname === "/" || /\.[^/]+$/.test(pathname)) return pathname;
  return `${pathname.replace(/\/+$/, "")}/`;
}

export function canonicalUrl(path = "/") {
  return absoluteUrl(canonicalPath(path));
}

export function pageUrl(path = "/") {
  return canonicalUrl(path);
}

function clean(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function imageUrl(image = seoConfig.defaultImage) {
  return absoluteUrl(image);
}

export function seoHead(input: SeoInput) {
  const title = clean(input.title);
  const description = clean(input.description);
  const url = canonicalUrl(input.path ?? "/");
  const image = imageUrl(input.image);
  const meta = [
    { title },
    { name: "description", content: description },
    {
      name: "robots",
      content: input.noindex ? "noindex, follow" : "index, follow",
    },
    {
      name: "googlebot",
      content: input.noindex ? "noindex, follow" : "index, follow",
    },
    { property: "og:type", content: input.type ?? "website" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:site_name", content: seoConfig.name },
    { property: "og:locale", content: seoConfig.locale },
    { property: "og:image", content: image },
    { property: "og:image:alt", content: input.imageAlt ?? title },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    { name: "twitter:image:alt", content: input.imageAlt ?? title },
    ...(input.publishedTime
      ? [{ property: "article:published_time", content: input.publishedTime }]
      : []),
    ...(input.modifiedTime
      ? [{ property: "article:modified_time", content: input.modifiedTime }]
      : []),
    ...(input.section
      ? [{ property: "article:section", content: input.section }]
      : []),
    ...(input.author ? [{ name: "author", content: input.author }] : []),
    ...(seoConfig.googleVerification
      ? [
          {
            name: "google-site-verification",
            content: seoConfig.googleVerification,
          },
        ]
      : []),
    ...(seoConfig.bingVerification
      ? [{ name: "msvalidate.01", content: seoConfig.bingVerification }]
      : []),
  ];

  return {
    meta,
    links: [{ rel: "canonical", href: url }],
  };
}

export function jsonLd(data: Record<string, unknown>) {
  return {
    type: "application/ld+json",
    children: JSON.stringify(data).replace(/</g, "\\u003c"),
  };
}

export function breadcrumbs(items: Breadcrumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: pageUrl(item.path),
    })),
  };
}

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: seoConfig.name,
    url: siteOrigin,
    logo: absoluteUrl("/images/logo.svg"),
  };
}

export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: seoConfig.name,
    url: siteOrigin,
    publisher: {
      "@type": "Organization",
      name: seoConfig.name,
      url: siteOrigin,
    },
  };
}

export function productJsonLd(product: Product, path: string) {
  const prices =
    product.plans?.flatMap((plan) => {
      const numeric = Number.parseFloat(plan.price.replace(/[^0-9.]/g, ""));
      return Number.isFinite(numeric) ? [numeric] : [];
    }) ?? [];
  const offers = prices.length
    ? {
        "@type": prices.length === 1 ? "Offer" : "AggregateOffer",
        priceCurrency: "USD",
        ...(prices.length === 1
          ? { price: prices[0] }
          : {
              lowPrice: Math.min(...prices),
              highPrice: Math.max(...prices),
              offerCount: prices.length,
            }),
        availability: "https://schema.org/InStock",
        url: pageUrl(path),
      }
    : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    brand: { "@type": "Brand", name: seoConfig.name },
    url: pageUrl(path),
    ...(offers ? { offers } : {}),
  };
}

export function categoryJsonLd(category: ProductCategory, path: string) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: category.name,
    description: category.description,
    url: pageUrl(path),
    mainEntity: {
      "@type": "ItemList",
      itemListElement: category.products.map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: product.name,
        url: pageUrl(product.href),
      })),
    },
  };
}
